import { useMemo } from 'react';
import useSWR from 'swr';
import { BaseAPI } from 'src/providers/base-api';
import { AuthType } from 'src/constant/common.interface';

// Fetcher function for SWR - calls the new v2 API
const permissionFetcherV2 = async () => {
  const response = await BaseAPI.getMyPermissionsV2();
  if (response.status === 200 && response?.data) {
    return response.data;
  }
  throw new Error('Failed to fetch permissions v2');
};

const DEBUG_LOGS = false;

/**
 * usePermissionsV2 - New RBAC hook using entity-based permissions
 *
 * This hook fetches permissions from the new v2 API which returns:
 * - entities: Map of entity permissions with CRUD flags
 * - features: Map of feature flags
 * - allPermissions: Array of all permission strings (e.g., "leave.create")
 *
 * @param featureName - Entity code(s) to check permissions for (e.g., "leave", "employee")
 * @param grantFullAccessIfMissing - If true, grant full access when entity not found
 * @returns { authorisation, isLoading, error }
 */
export const usePermissionsV2 = (featureName: string | string[], grantFullAccessIfMissing = false) => {
  // SWR automatically caches based on the key
    const { data, error, isLoading } = useSWR(
        'permissions-v2-me',
        permissionFetcherV2,
        {
    revalidateOnFocus: false, // Don't re-fetch when switching tabs
            revalidateIfStale: true,  // Use cached data but refresh if stale
            revalidateOnMount: true,  // Refresh when mounting
            dedupingInterval: 60000,   // Cache for 60 seconds
            shouldRetryOnError: false, // Don't retry on 403/auth errors — prevents request storms
        }
    );

  if (error) {
    console.error('usePermissionsV2: SWR fetch error', error);
  }

  const featureNames = Array.isArray(featureName) ? featureName : [featureName];

  // Helper to normalize strings for matching (lowercase, no spaces/underscores/dashes)
  const normalize = (s: string) =>
    (s || '')
      .toString()
      .toLowerCase()
      .replace(/[\s_-]/g, '');

  // Memoize authorisation to prevent creating new object on every render
  const authorisation = useMemo<AuthType>(() => {
    const normFeatureNames = featureNames.map(normalize);

    if (DEBUG_LOGS) console.log(`usePermissionsV2 checking for [${featureNames.join(', ')}]`, { hasData: !!data });

    let auth: AuthType = {
      isfullAccess: false,
      isCreate: false,
      isView: false,
      isEdit: false,
      isDelete: false,
      isManageAll: false,
      isShow: false
    };

    if (data?.entities) {
      if (DEBUG_LOGS) console.log('usePermissionsV2 data (entities):', data.entities);

      // Check if any of the requested feature names match an entity (by code, name, or legacy names)
      let foundEntity = false;
      for (const entityCode of Object.keys(data.entities)) {
        const entity = data.entities[entityCode];
        const normEntityCode = normalize(entityCode);
        const normEntityName = normalize(entity.name || '');

        // Also check legacy module names for backward compatibility
        const normLegacyNames = (entity.legacyModuleNames || []).map(normalize);
        const matchesEntity =
          normFeatureNames.includes(normEntityCode) ||
          normFeatureNames.includes(normEntityName) ||
          normFeatureNames.some((name) => normLegacyNames.includes(name));

        if (matchesEntity) {
          foundEntity = true;
          const flags = entity.flags;

          // Map the new flags structure to the legacy AuthType
          auth = {
            isfullAccess: flags.isFullAccess || false,
            isCreate: flags.isCreate || false,
            isView: flags.isView || false,
            isEdit: flags.isEdit || false,
            isDelete: flags.isDelete || false,
            isManageAll: flags.isManageAll || false,
            isShow: flags.isView || flags.isCreate || flags.isEdit || flags.isDelete || flags.isFullAccess
          };

          // If full access is found, no need to check other entities
          if (flags.isFullAccess) {
            break;
          }
        }
      }

      // Check feature flags as well (for backward compatibility with feature-based permissions)
      if (!foundEntity && data.features) {
        for (const featureName of normFeatureNames) {
          if (data.features[featureName]) {
            auth.isShow = true;
            // Feature flags are typically boolean, so we grant view access
            auth.isView = true;
            foundEntity = true;
          }
        }
      }

      // Grant full access if entity not found and grantFullAccessIfMissing is true
      if (grantFullAccessIfMissing && !foundEntity) {
        auth = {
          isfullAccess: true,
          isCreate: true,
          isView: true,
          isEdit: true,
          isDelete: true,
          isManageAll: false,
          isShow: true
        };
      }
    }

    return auth;
  }, [data, featureName, grantFullAccessIfMissing]);

  return { authorisation, isLoading, error };
};
